import React, { memo, useEffect, useMemo, useRef, useState } from 'react';
import Chart from 'chart.js/auto';
import AtlasChartRuntime from './atlasChartRuntime';
import useAtlasTheme from '../useAtlasTheme';
import './AtlasChartCard.css';

// Card for Atlas charts that carry `config.meta` (viz style v1; see atlas-backend docs/atlas-viz-style.md).
// All styling decisions (colors per role, formats, plugins) come from the shared runtime; this component only
// lays out the card chrome and owns the Chart.js instance. Plugins are passed per chart by the runtime, never
// registered globally, and Chart.defaults is never touched (ChartComponent's legacy pages depend on it).

function pluginText(options, key) {
  const plugin = options?.plugins?.[key];
  if (!plugin || plugin.display === false) return '';
  const { text } = plugin;
  if (Array.isArray(text)) return text.filter(Boolean).join(' ');
  return typeof text === 'string' ? text : '';
}

function attempt(fn, fallback) {
  try {
    return fn();
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[AtlasChartCard]', e);
    return fallback;
  }
}

function LegendKey({ mark, color }) {
  const style = mark === 'line' || mark === 'dash' ? { borderColor: color } : { background: color };
  return <i className={`atlas-vcard-key atlas-vcard-key--${mark}`} style={style} aria-hidden="true" />;
}

function AtlasChartCard({ config, variant = 'chat' }) {
  const theme = useAtlasTheme();
  const canvasRef = useRef(null);
  const [view, setView] = useState('chart');
  const [renderError, setRenderError] = useState(null);

  const meta = config?.meta || {};
  const title = meta.title || pluginText(config?.options, 'title');
  const subtitle = meta.subtitle || pluginText(config?.options, 'subtitle');
  const legend = useMemo(() => attempt(() => AtlasChartRuntime.legend(config, theme), []), [config, theme]);
  const table = useMemo(() => attempt(() => AtlasChartRuntime.table(config), null), [config]);
  const hasTable = !!(table && Array.isArray(table.rows) && table.rows.length > 0);
  const showTable = hasTable && view === 'table';
  const bodyHeight = attempt(() => AtlasChartRuntime.bodyHeight(config), 270);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !config) return undefined;
    let chart = null;
    setRenderError(null);
    try {
      chart = new Chart(canvas, AtlasChartRuntime.prepare(config, { theme }));
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[AtlasChartCard] chart failed to render:', e);
      setRenderError(e?.message || 'Chart failed to render.');
    }
    return () => {
      if (!chart) return;
      try {
        chart.destroy();
      } catch {
        // ignore: canvas already detached
      }
    };
  }, [config, theme]);

  const showBar = legend.length > 0 || hasTable;

  return (
    <div className={`atlas-vcard atlas-vcard--${variant}`}>
      {title ? <div className="atlas-vcard-title">{title}</div> : null}
      {subtitle ? <div className="atlas-vcard-sub">{subtitle}</div> : null}
      {meta.capped_note ? <div className="atlas-vcard-note">{meta.capped_note}</div> : null}

      {showBar ? (
        <div className="atlas-vcard-bar">
          {legend.length > 0 ? (
            <div className="atlas-vcard-legend">
              {legend.map((item, i) => (
                <span className="atlas-vcard-legend-item" key={`${i}_${item.label}`}>
                  <LegendKey mark={item.mark} color={item.color} />
                  <span className="atlas-vcard-legend-label">{item.label}</span>
                </span>
              ))}
            </div>
          ) : null}
          {hasTable ? (
            <div className="atlas-vcard-seg" role="group" aria-label="View">
              <button type="button" aria-pressed={view === 'chart'} onClick={() => setView('chart')}>
                Chart
              </button>
              <button type="button" aria-pressed={view === 'table'} onClick={() => setView('table')}>
                Table
              </button>
            </div>
          ) : null}
        </div>
      ) : null}

      {/* The canvas stays mounted while the table is showing so the chart does not rebuild on toggle. */}
      <div className="atlas-vcard-body" style={{ height: bodyHeight }} hidden={showTable}>
        <canvas ref={canvasRef} role="img" aria-label={title || 'Chart'} />
        {renderError ? <div className="atlas-vcard-error">Chart failed to render.</div> : null}
      </div>

      {showTable ? (
        <div className="atlas-vcard-table" role="region" aria-label={`${title || 'Chart'} (table)`} tabIndex={0}>
          <table>
            <thead>
              <tr>
                {table.columns.map((col, i) => (
                  <th key={i} scope="col">
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {table.rows.map((row, r) => (
                <tr key={r}>
                  {row.map((cell, c) => (
                    <td key={c}>{cell}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {meta.footnote ? (
        <div className="atlas-vcard-foot">
          <span>{meta.footnote}</span>
        </div>
      ) : null}
    </div>
  );
}

export default memo(AtlasChartCard);
